/**
 * Voice Input Component
 * Enables voice-based document queries with Quick Actions
 * Features: Custom commands, TTS feedback, context-aware commands
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Mic, MicOff, Square, Loader2, Volume2, VolumeX,
  ArrowRight, Search, FileText, AlertTriangle, 
  Clock, Upload, HelpCircle, CheckCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import agenticApi from '../../api/agenticApi';

const VoiceInput = ({ headers, onTranscription, onCommand, language = 'en', enableTTS = true }) => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState(null);
  const [command, setCommand] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(enableTTS);
  const [isPlayingTTS, setIsPlayingTTS] = useState(false);
  const [contextSuggestions, setContextSuggestions] = useState([]);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecordingCleanup();
    };
  }, []);

  // Load contextual suggestions on mount
  useEffect(() => {
    loadContextSuggestions();
  }, []);

  const stopRecordingCleanup = () => {
    // Clear any timeout
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    
    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.log('MediaRecorder already stopped');
      }
    }
    
    // Stop all tracks in the stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    
    setIsRecording(false);
  };

  const loadContextSuggestions = async () => {
    try {
      const response = await agenticApi.getContextualSuggestions(headers);
      setContextSuggestions(response.data.suggestions || []);
    } catch (err) {
      // Silently fail - suggestions are optional
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Cleanup stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        await processAudio(blob);
      };
      
      mediaRecorderRef.current.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        stopRecordingCleanup();
        toast.error('Recording error occurred');
      };
      
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setCommand(null);
      toast.info('Listening... Speak your command');
      
      // Auto-stop after 30 seconds (safety timeout)
      recordingTimeoutRef.current = setTimeout(() => {
        if (isRecording) {
          toast.info('Recording stopped (30s limit)');
          stopRecording();
        }
      }, 30000);
      
    } catch (err) {
      console.error('Microphone access error:', err);
      toast.error('Could not access microphone. Please check permissions.');
      stopRecordingCleanup();
    }
  };

  const stopRecording = () => {
    // Clear timeout
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    
    // Always attempt to stop - don't rely on isRecording state (stale closure)
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      console.log('Error stopping recorder:', e);
    }
    
    // Clean up the stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setIsRecording(false);
  };

  const processAudio = async (blob) => {
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      if (language) {
        formData.append('language', language);
      }
      
      // Use the full execute endpoint for command detection
      const response = await agenticApi.executeVoiceCommand(formData, headers);
      
      const transcribedText = response.data.transcription?.text || '';
      setTranscription({
        text: transcribedText,
        language: response.data.transcription?.language,
        duration: response.data.transcription?.duration
      });
      
      // Try enhanced command parsing (with context + custom commands)
      let commandResult = response.data;
      try {
        const enhancedResponse = await agenticApi.enhancedVoiceCommand(transcribedText, headers);
        commandResult = enhancedResponse.data;
      } catch (enhancedErr) {
        // Fall back to basic command parsing
        console.log('Enhanced command not available, using basic');
      }
      
      // Set command result
      setCommand(commandResult);
      
      // Handle the command
      handleCommand(commandResult);
      
      // Play TTS feedback if enabled
      if (ttsEnabled && commandResult.command_type !== 'unknown') {
        playTTSFeedback(commandResult.command_type, commandResult.description);
      }
      
      if (onTranscription) {
        onTranscription({ text: transcribedText });
      }
      
    } catch (err) {
      console.error('Voice command error:', err);
      
      // Try browser-based Speech Recognition as fallback for any backend error
      const isBackendError = !err.response || err.response?.status >= 400;
      if (isBackendError) {
        try {
          const text = await browserSpeechFallback();
          if (text) {
            setTranscription({ text, language: language, duration: 0 });
            toast.success('Transcription complete (browser mode)');
            if (onTranscription) {
              onTranscription({ text });
            }
          } else {
            toast.error('Voice recognition failed. Please type your query instead.');
          }
        } catch (fallbackErr) {
          toast.error('Voice recognition unavailable. Please type your query instead.');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to process voice command');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const browserSpeechFallback = () => {
    return new Promise((resolve, reject) => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        resolve(null);
        return;
      }
      
      const recognition = new SpeechRecognition();
      recognition.lang = language || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      
      let hasResult = false;
      
      recognition.onresult = (event) => {
        hasResult = true;
        const text = event.results[0][0].transcript;
        resolve(text);
      };
      
      recognition.onerror = (event) => {
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access in browser settings.');
        }
        if (!hasResult) resolve(null);
      };
      
      recognition.onend = () => {
        if (!hasResult) resolve(null);
      };
      
      // Actually start listening for speech
      toast.info('Browser voice mode: Speak now...');
      recognition.start();
    });
  };

  const playTTSFeedback = async (commandType, description) => {
    if (!ttsEnabled) return;
    
    try {
      setIsPlayingTTS(true);
      const response = await agenticApi.ttsForCommand(commandType, description, 'nova', headers);
      
      if (response.data.success && response.data.audio_data) {
        // Convert base64 to audio blob
        const audioData = atob(response.data.audio_data);
        const arrayBuffer = new ArrayBuffer(audioData.length);
        const view = new Uint8Array(arrayBuffer);
        for (let i = 0; i < audioData.length; i++) {
          view[i] = audioData.charCodeAt(i);
        }
        
        const blob = new Blob([arrayBuffer], { type: `audio/${response.data.audio_format || 'mp3'}` });
        const url = URL.createObjectURL(blob);
        
        // Play audio
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.play();
        } else {
          const audio = new Audio(url);
          audio.onended = () => {
            setIsPlayingTTS(false);
            URL.revokeObjectURL(url);
          };
          audio.onerror = () => {
            setIsPlayingTTS(false);
            URL.revokeObjectURL(url);
          };
          audio.play();
        }
      }
    } catch (err) {
      console.log('TTS feedback not available:', err.message);
    } finally {
      setTimeout(() => setIsPlayingTTS(false), 3000); // Fallback timeout
    }
  };

  const handleCommand = (cmd) => {
    if (!cmd || cmd.command_type === 'unknown') {
      toast.info('Command not recognized. Try "help" for available commands.');
      return;
    }

    const { action, target, description, command_type } = cmd;
    
    // Show description toast
    toast.success(description);
    
    // Execute action
    if (action === 'navigate' && target) {
      setTimeout(() => {
        navigate(target);
        if (cmd.tab) {
          // Store tab preference for the page
          sessionStorage.setItem(`${target}_tab`, cmd.tab);
        }
      }, 500);
    } else if (action === 'search' && cmd.query) {
      if (onCommand) {
        onCommand(cmd);
      } else {
        // Navigate to search with query
        navigate(`/search?q=${encodeURIComponent(cmd.query)}`);
      }
    } else if (action === 'chat') {
      // Pass to chat handler
      if (onCommand) {
        onCommand(cmd);
      }
    } else if (action === 'show_help') {
      setShowHelp(true);
    }
    
    // If context was resolved, show additional info
    if (cmd.context_resolved && cmd.resolved_document) {
      toast.info(`Context: "${cmd.resolved_document.title || 'document'}"`);
    }
  };

  const parseTextCommand = async (text) => {
    if (!text.trim()) return;
    
    setIsProcessing(true);
    try {
      // Use enhanced command for context-awareness
      const response = await agenticApi.enhancedVoiceCommand(text, headers);
      setCommand(response.data);
      handleCommand(response.data);
      
      if (ttsEnabled && response.data.command_type !== 'unknown') {
        playTTSFeedback(response.data.command_type, response.data.description);
      }
    } catch (err) {
      console.error('Command parse error:', err);
      toast.error('Failed to process command');
    } finally {
      setIsProcessing(false);
    }
  };

  const getCommandIcon = (type) => {
    switch (type) {
      case 'show_tasks': return <Clock className="w-4 h-4" />;
      case 'search_documents':
      case 'search': return <Search className="w-4 h-4" />;
      case 'recent_documents': return <FileText className="w-4 h-4" />;
      case 'expiring_documents': return <AlertTriangle className="w-4 h-4" />;
      case 'security_alerts': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'upload': return <Upload className="w-4 h-4" />;
      case 'help': return <HelpCircle className="w-4 h-4" />;
      default: return <ArrowRight className="w-4 h-4" />;
    }
  };

  const helpCommands = [
    { cmd: "Show my tasks", desc: "View pending approvals" },
    { cmd: "Show recent documents", desc: "Latest uploads" },
    { cmd: "Find contracts from last month", desc: "Search with filters" },
    { cmd: "What documents expire soon", desc: "Expiring documents" },
    { cmd: "Search for [query]", desc: "Document search" },
    { cmd: "Go to dashboard", desc: "Navigate pages" },
  ];

  return (
    <Card data-testid="voice-input-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-blue-600" />
            Voice Commands
            {isPlayingTTS && (
              <Volume2 className="w-4 h-4 text-green-500 animate-pulse" />
            )}
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setTtsEnabled(!ttsEnabled)}
              title={ttsEnabled ? "Disable voice feedback" : "Enable voice feedback"}
            >
              {ttsEnabled ? (
                <Volume2 className="w-4 h-4 text-green-600" />
              ) : (
                <VolumeX className="w-4 h-4 text-slate-400" />
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowHelp(!showHelp)}
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Context Suggestions */}
        {contextSuggestions.length > 0 && !showHelp && (
          <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-800 mb-2 text-xs flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Based on your activity:
            </h4>
            <div className="space-y-1">
              {contextSuggestions.slice(0, 3).map((s, i) => (
                <button
                  key={i}
                  onClick={() => parseTextCommand(s.command)}
                  className="block text-left w-full text-xs p-1.5 hover:bg-purple-100 rounded transition-colors"
                >
                  <span className="font-medium text-purple-700">"{s.command}"</span>
                  <span className="text-purple-600 ml-1">{s.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Help Panel */}
        {showHelp && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2 text-sm">Available Commands:</h4>
            <div className="grid grid-cols-2 gap-2">
              {helpCommands.map((h, i) => (
                <div key={i} className="text-xs">
                  <span className="font-medium text-blue-700">"{h.cmd}"</span>
                  <span className="text-blue-600 block">{h.desc}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-blue-200">
              <p className="text-xs text-blue-600">
                <strong>Context-aware:</strong> Say "summarize that document" to reference your last viewed doc
              </p>
            </div>
          </div>
        )}

        {/* Hidden audio element for TTS */}
        <audio ref={audioRef} className="hidden" onEnded={() => setIsPlayingTTS(false)} />

        <div className="flex flex-col items-center gap-4">
          {/* Recording Button */}
          <div className="relative">
            {isRecording && (
              <div className="absolute inset-0 animate-ping rounded-full bg-red-400 opacity-30" />
            )}
            <Button
              size="lg"
              variant={isRecording ? "destructive" : "default"}
              className={`w-20 h-20 rounded-full ${
                isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              data-testid="voice-record-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : isRecording ? (
                <Square className="w-8 h-8" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </Button>
          </div>
          
          {/* Status */}
          <p className="text-sm text-slate-600">
            {isProcessing ? 'Processing command...' : isRecording ? 'Listening... Click to stop' : 'Click to speak a command'}
          </p>
          
          {/* Command Result */}
          {command && command.command_type !== 'unknown' && (
            <div className="w-full p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {getCommandIcon(command.command_type)}
                    <Badge variant="outline" className="text-xs bg-green-100">
                      {command.command_type.replace(/_/g, ' ')}
                    </Badge>
                    {command.is_custom && (
                      <Badge variant="secondary" className="text-xs">Custom</Badge>
                    )}
                    {command.context_resolved && (
                      <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">Context</Badge>
                    )}
                    {command.confidence && (
                      <span className="text-xs text-slate-500">
                        {Math.round(command.confidence * 100)}% confident
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-green-800">{command.description}</p>
                  {transcription?.text && (
                    <p className="text-xs text-slate-500 mt-1 italic">
                      "{transcription.text}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Unknown Command */}
          {command && command.command_type === 'unknown' && transcription?.text && (
            <div className="w-full p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <HelpCircle className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Command not recognized
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Heard: "{transcription.text}"
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Try saying "help" for available commands
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VoiceInput;
